<template>
   <div>
      <PageTitle :title="title" />
      <AssignVendorModal :expense-type="expenseType" />
      <AddVendorModal :expense-type="expenseType" />
      <div class="d-flex align-items-start justify-content-end mb-4">
         <b-button
            v-if="hasCompletedEntry"
            class="mr-2 dark-button"
            variant="primary"
            style="font-size: 12px; padding: 5px 20px"
            @click="unlock"
         >
            Unlock
         </b-button>
         <b-button
            id="btn-add-vendor"
            class="mr-2 dark-button"
            variant="primary"
            style="font-size: 12px; padding: 5px 20px"
            :disabled="!hasDeclarationByEntityType || isCompleted"
            v-if="!isSME"
            @click="showModal"
         >
            {{ addManageLabel }}
         </b-button>
         <b-button
            id="btn-assign-vendor"
            variant="primary"
            class="button dark-button"
            :disabled="!hasVendors || isCompleted"
            @click="showAssignModal"
            v-if="!isSME && hasPeriodIds"
            style="padding: 5px 20px; font-size: 12px"
         >
            {{ assignLabel }}
         </b-button>
      </div>
   </div>
</template>

<script>
import {mapGetters} from 'vuex';
import AddVendorModal from './AddVendorModal.vue';
import AssignVendorModal from './AssignVendorModal.vue';
import {EXPENSE_ENTITY_LABEL_CONFIG, TITLE_CONFIG} from '../../../helpers/baseConfig';
import {expensePermissionsMixin} from '@/mixins';

export default {
   name: 'ExpensesHeader',
   components: {AssignVendorModal, AddVendorModal},
   mixins: [expensePermissionsMixin],
   props: {
      expenseType: {
         type: String,
         required: true,
         validator: (value) => ['cloud', 'supplies', 'contractors', 'employees'].includes(value),
      },
   },
   computed: {
      ...mapGetters({
         isSME: 'isSME',
         isCustomer: 'isCustomer',
      }),

      isCompleted() {
         return this.$store.getters[`${this.expenseType}/isCompleted`];
      },
      declaration() {
         return this.$store.getters[`${this.expenseType}/declaration`];
      },
      manualVendors() {
         return this.$store.getters[`${this.expenseType}/manualVendors`];
      },
      importedVendors() {
         return this.$store.getters[`${this.expenseType}/importedVendors`];
      },

      title() {
         return TITLE_CONFIG[this.expenseType];
      },

      entityLabelPlural() {
         return (
            EXPENSE_ENTITY_LABEL_CONFIG[this.expenseType]?.plural ||
            EXPENSE_ENTITY_LABEL_CONFIG.supplies.plural
         );
      },

      assignLabel() {
         return `Assign ${this.entityLabelPlural}`;
      },

      hasVendors() {
         return this.manualVendors.length || this.importedVendors.length;
      },

      periodIds() {
         return this.$store.getters[`${this.expenseType}/supportedPeriodIds`];
      },

      hasPeriodIds() {
         return this.periodIds.length > 0;
      },

      hasInSingleUSStates() {
         return this.declaration === 'ONE';
      },

      hasInManyUSStates() {
         return this.declaration === 'MANY';
      },

      hasDeclarationByEntityType() {
         if (this.expenseType === 'employees') {
            // Employees have no declaration concept (see docs/legacy/employee-sunset-discovery.md §7a) —
            // every company has employees, so there's no "have you used employees" gate to satisfy.
            return true;
         } else if (this.expenseType === 'supplies') {
            return this.hasInSingleUSStates || this.hasInManyUSStates;
         } else {
            return this.declaration;
         }
      },

      hasCompletedEntry() {
         return this.isRndig && this.isCompleted && this.hasDeclarationByEntityType;
      },

      assignedUsers() {
         return [...this.manualVendors, ...this.importedVendors].filter(
            ({assigneeId}) => assigneeId !== null
         );
      },

      showAssign() {
         return (
            this.hasPeriodIds &&
            this.assignedUsers.length === 0 &&
            this.isCustomer &&
            !this.isCompleted &&
            (this.importedVendors.length > 0 || this.manualVendors.length > 0)
         );
      },
   },
   methods: {
      showModal() {
         this.$bvModal.show(`${this.expenseType}-add-vendor-modal`);
      },
      showAssignModal() {
         this.$bvModal.show('modal-assign-vendor');
      },
      async unlock() {
         const msg =
            "Unlocking this section may delay your credit calculation. You will have to mark the section complete again once you're done.";
         const title = 'Unlock this section?';
         const proceed = await this.$bvModal.msgBoxConfirm(msg, {
            title,
            centered: true,
         });
         if (proceed) {
            await this.blockingRequest(`${this.expenseType}/unlock`, {
               companyId: this.$companyId,
            });
         }
      },
   },

   async mounted() {
      if (this.showAssign) {
         this.showAssignModal();
      }
   },

   watch: {
      async activeStudyId(newVal, oldVal) {
         if (!newVal || Number(newVal) === Number(oldVal)) {
            return;
         }

         if (this.showAssign) {
            this.showAssignModal();
         }
      },
   },
};
</script>

<style scoped lang="scss"></style>
